/**
 * Account page: handles Google session_id callback, login/register, and the
 * authenticated dashboard with tabs.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useLang, assetUrl } from "../lib/providers";
import { useCustomer } from "../lib/customerAuth";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Package, User, Gift, MapPin, Settings as SettingsIcon, LogOut, Download, Copy, Trash2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "orders", label_fr: "Mes commandes", label_ar: "طلباتي", Icon: Package },
  { id: "profile", label_fr: "Mon profil", label_ar: "ملفي", Icon: User },
  { id: "referral", label_fr: "Parrainage", label_ar: "الإحالة", Icon: Gift },
  { id: "addresses", label_fr: "Mes adresses", label_ar: "عناويني", Icon: MapPin },
  { id: "settings", label_fr: "Paramètres", label_ar: "الإعدادات", Icon: SettingsIcon },
];

export default function MonComptePage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { user, loading, login, register, forgotPassword, resendConfirmation, googleSignIn, exchangeGoogle, logout } = useCustomer();
  const [tab, setTab] = useState("orders");

  // Handle Google callback (session_id in URL fragment)
  const processedRef = useRef(false);
  useEffect(() => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (m && !processedRef.current) {
      processedRef.current = true;
      exchangeGoogle(m[1])
        .then(() => {
          // Clean URL fragment
          window.history.replaceState({}, document.title, window.location.pathname);
          toast.success(lang === "ar" ? "مرحبا!" : "Bienvenue !");
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Auth error"));
    }
  }, [exchangeGoogle, lang]);

  if (loading) {
    return <div className="min-h-screen text-white flex items-center justify-center">…</div>;
  }

  return (
    <div className="min-h-screen text-white bg-court" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Retour à l'accueil — visible sur toutes les vues (login ET dashboard) */}
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-neon transition-colors mb-6"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "ar" ? "العودة إلى الصفحة الرئيسية" : "Retour à l'accueil"}
        </button>
        {!user ? (
          <AuthGate lang={lang} login={login} register={register} forgotPassword={forgotPassword} resendConfirmation={resendConfirmation} googleSignIn={googleSignIn} />
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
              <div>
                <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl" data-testid="account-title">
                  {lang === "ar" ? `أهلا، ${user.name}` : `Bonjour, ${user.name}`}
                </h1>
                <p className="text-xs text-white/50 mt-1">{user.email}</p>
              </div>
              <Button onClick={() => logout().then(() => navigate("/"))} variant="outline" className="border-white/20 rounded-none uppercase tracking-widest text-xs" data-testid="logout-btn">
                <LogOut className="w-4 h-4 me-2" />{lang === "ar" ? "تسجيل خروج" : "Déconnexion"}
              </Button>
            </div>

            <div className="flex gap-2 overflow-x-auto mb-8 border-b border-white/10 pb-1">
              {TABS.map(({ id, label_fr, label_ar, Icon }) => (
                <button
                  key={id}
                  onClick={() => setTab(id)}
                  className={`shrink-0 px-3 py-2 text-[11px] font-black uppercase tracking-widest border transition-colors flex items-center gap-2 ${
                    tab === id ? "bg-neon text-black border-neon" : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
                  }`}
                  data-testid={`tab-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {lang === "ar" ? label_ar : label_fr}
                </button>
              ))}
            </div>

            {tab === "orders" && <OrdersTab lang={lang} />}
            {tab === "profile" && <ProfileTab lang={lang} user={user} />}
            {tab === "referral" && <ReferralTab lang={lang} />}
            {tab === "addresses" && <AddressesTab lang={lang} />}
            {tab === "settings" && <SettingsTab lang={lang} navigate={navigate} />}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function AuthGate({ lang, login, register, forgotPassword, resendConfirmation, googleSignIn }) {
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "", newsletter: true });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);     // email of just-registered (pending) user
  const [forgotSent, setForgotSent] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(null); // email of user trying to login but pending

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success(lang === "ar" ? "تم!" : "Connecté !");
      } else if (mode === "register") {
        const res = await register({
          email: form.email,
          password: form.password,
          name: form.name,
          phone: form.phone,
          newsletter_opt_in: !!form.newsletter,
          lang,
        });
        setPending(res?.email || form.email);
      } else if (mode === "forgot") {
        await forgotPassword(form.email, lang);
        setForgotSent(true);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "EMAIL_NOT_CONFIRMED") {
        setNeedsConfirm(form.email);
      } else {
        toast.error(typeof detail === "string" ? detail : (lang === "ar" ? "خطأ" : "Erreur"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendConfirmation(needsConfirm || pending || form.email, lang);
      toast.success(lang === "ar" ? "✅ تم إعادة الإرسال" : "✅ Email renvoyé. Pensez à vérifier vos spams.");
    } catch {
      toast.error("Erreur");
    }
  };

  // === PENDING state (just registered) ===
  if (pending) {
    return (
      <div className="max-w-md mx-auto pt-8 text-center" data-testid="auth-pending">
        <div className="text-5xl mb-4">📧</div>
        <h1 className="font-heading font-black uppercase tracking-tight text-2xl mb-3">
          {lang === "ar" ? "تحقق من بريدك الإلكتروني" : "Vérifiez votre email"}
        </h1>
        <p className="text-sm text-white/80 mb-2">
          {lang === "ar" ? `لقد أرسلنا رابط تأكيد إلى ${pending}` : `Nous avons envoyé un lien de confirmation à ${pending}`}
        </p>
        <div className="my-5 p-4 border border-amber-500/30 bg-amber-500/10 text-xs text-amber-200">
          ⚠️ <strong>{lang === "ar" ? "هام:" : "Important :"}</strong> {lang === "ar"
            ? "إذا لم تتلق البريد الإلكتروني خلال دقيقتين، تحقق من مجلد البريد العشوائي (Hotmail / Outlook خصوصا)."
            : "Si vous ne recevez rien sous 2 min, vérifiez votre dossier spam / indésirables (surtout sur Hotmail / Outlook)."}
        </div>
        <button onClick={handleResend} className="text-xs underline text-neon hover:text-white" data-testid="resend-confirm-btn">
          {lang === "ar" ? "إعادة إرسال البريد الإلكتروني" : "Renvoyer l'email de confirmation"}
        </button>
        <button onClick={() => { setPending(null); setMode("login"); }} className="block mt-4 mx-auto text-xs text-white/50 hover:text-neon underline">
          {lang === "ar" ? "العودة" : "Retour à la connexion"}
        </button>
      </div>
    );
  }

  // === FORGOT-PASSWORD SENT state ===
  if (forgotSent) {
    return (
      <div className="max-w-md mx-auto pt-8 text-center" data-testid="forgot-sent">
        <div className="text-5xl mb-4">🔑</div>
        <h1 className="font-heading font-black uppercase tracking-tight text-2xl mb-3">
          {lang === "ar" ? "تحقق من بريدك الإلكتروني" : "Vérifiez votre email"}
        </h1>
        <p className="text-sm text-white/80 mb-2">
          {lang === "ar" ? "إذا كان البريد الإلكتروني موجودا في قاعدة بياناتنا، فستتلقى رابط إعادة التعيين خلال دقيقة."
                        : "Si cette adresse existe, vous recevrez un lien de réinitialisation sous 1 min."}
        </p>
        <div className="my-5 p-4 border border-amber-500/30 bg-amber-500/10 text-xs text-amber-200">
          ⚠️ {lang === "ar"
            ? "وصول رابط الاسترداد قد يستغرق دقيقتين. تحقق من البريد العشوائي (Hotmail / Outlook)."
            : "L'email peut mettre jusqu'à 2 min. Pensez aussi à vérifier vos spams (Hotmail / Outlook)."}
        </div>
        <button onClick={() => { setForgotSent(false); setMode("login"); }} className="text-xs text-neon underline">
          {lang === "ar" ? "العودة إلى تسجيل الدخول" : "Retour à la connexion"}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto pt-8" data-testid="auth-gate">
      <h1 className="font-heading font-black uppercase tracking-tight text-3xl sm:text-4xl text-center mb-2">
        {lang === "ar" ? "حسابي" : "Mon compte"}
      </h1>
      <p className="text-white/50 text-sm text-center mb-8">
        {lang === "ar" ? "اختياري — تستطيع الطلب بدون حساب" : "Optionnel — vous pouvez commander sans compte"}
      </p>

      <Button onClick={googleSignIn} className="w-full bg-white text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest py-5 h-auto mb-2" data-testid="google-signin-btn">
        <svg className="w-4 h-4 me-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h5.9c-.3 1.4-1 2.6-2.2 3.4v2.8h3.6c2.1-1.9 3.2-4.8 3.2-8.4z"/><path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.8c-1 .7-2.3 1.1-3.6 1.1-2.7 0-5.1-1.8-5.9-4.3H2.4v2.8C4.2 20.6 7.8 23 12 23z"/><path fill="#FBBC05" d="M6.1 14.4c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.3H2.4C1.6 8.8 1.2 10.4 1.2 12.3s.4 3.5 1.2 5l3.7-2.9z"/><path fill="#EA4335" d="M12 6.4c1.5 0 2.9.5 3.9 1.5l3.2-3.2C17.4 2.9 14.9 1.9 12 1.9 7.8 1.9 4.2 4.3 2.4 7.7l3.7 2.9c.8-2.5 3.2-4.2 5.9-4.2z"/></svg>
        {lang === "ar" ? "متابعة باستعمال Google" : "Continuer avec Google"}
      </Button>
      <p className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40 mb-4" data-testid="google-secure-badge">
        <ShieldCheck className="w-3 h-3 text-neon" />
        {lang === "ar" ? "تسجيل دخول آمن" : "Login sécurisé"}
      </p>

      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[10px] uppercase tracking-widest text-white/40">{lang === "ar" ? "أو" : "ou"}</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <div className="space-y-3 border border-white/10 p-5">
        {mode !== "forgot" && (
          <div className="flex gap-2 text-xs uppercase tracking-widest">
            <button onClick={() => { setMode("login"); setNeedsConfirm(null); }} className={`flex-1 py-2 ${mode === "login" ? "bg-neon text-black" : "border border-white/10 text-white/60"}`} data-testid="tab-login">
              {lang === "ar" ? "تسجيل الدخول" : "Connexion"}
            </button>
            <button onClick={() => { setMode("register"); setNeedsConfirm(null); }} className={`flex-1 py-2 ${mode === "register" ? "bg-neon text-black" : "border border-white/10 text-white/60"}`} data-testid="tab-register">
              {lang === "ar" ? "إنشاء حساب" : "Créer un compte"}
            </button>
          </div>
        )}

        {mode === "register" && (
          <>
            <Field label={lang === "ar" ? "الاسم" : "Nom"}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="field-name" /></Field>
            <Field label={lang === "ar" ? "هاتف" : "Téléphone"}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0555..." className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="field-phone" /></Field>
          </>
        )}

        <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="field-email" /></Field>

        {mode !== "forgot" && (
          <Field label={lang === "ar" ? "كلمة المرور" : "Mot de passe"}><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="field-password" /></Field>
        )}

        {mode === "register" && (
          <label className="flex items-start gap-2 cursor-pointer text-xs text-white/80 select-none">
            <input
              type="checkbox"
              checked={!!form.newsletter}
              onChange={(e) => setForm({ ...form, newsletter: e.target.checked })}
              className="accent-neon mt-0.5 w-4 h-4"
              data-testid="field-newsletter-opt-in"
            />
            <span>
              {lang === "ar"
                ? "🎁 أرغب في تلقي العروض الحصرية والحصول على رمز ترحيبي -10٪."
                : "🎁 Je souhaite recevoir les offres exclusives et mon code de bienvenue -10%."}
            </span>
          </label>
        )}

        {needsConfirm && (
          <div className="border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="needs-confirm-banner">
            <p>
              {lang === "ar"
                ? "حسابك لم يتم تأكيده بعد. تحقق من بريدك الإلكتروني (والبريد العشوائي)."
                : "Votre compte n'est pas encore confirmé. Vérifiez votre boîte email (et les spams)."}
            </p>
            <button onClick={handleResend} className="mt-2 underline text-neon" data-testid="needs-confirm-resend">
              {lang === "ar" ? "إعادة إرسال البريد الإلكتروني" : "Renvoyer l'email de confirmation"}
            </button>
          </div>
        )}

        <Button
          onClick={submit}
          disabled={busy || !form.email || (mode !== "forgot" && !form.password)}
          className="w-full bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest py-5 h-auto disabled:opacity-40"
          data-testid="auth-submit"
        >
          {busy ? "…" : mode === "login"
            ? (lang === "ar" ? "تسجيل الدخول" : "Se connecter")
            : mode === "register"
            ? (lang === "ar" ? "إنشاء" : "Créer mon compte")
            : (lang === "ar" ? "إرسال رابط" : "Recevoir un lien")}
        </Button>

        {mode === "login" && (
          <button
            type="button"
            onClick={() => setMode("forgot")}
            className="w-full text-center text-xs text-white/60 hover:text-neon underline pt-1"
            data-testid="forgot-password-link"
          >
            {lang === "ar" ? "نسيت كلمة المرور ؟" : "🔑 Mot de passe oublié ?"}
          </button>
        )}

        {mode === "forgot" && (
          <button
            type="button"
            onClick={() => setMode("login")}
            className="w-full text-center text-xs text-white/60 hover:text-neon underline pt-1"
            data-testid="back-to-login"
          >
            {lang === "ar" ? "العودة" : "← Retour"}
          </button>
        )}

        {mode === "register" && (
          <p className="text-[10px] text-white/40 text-center mt-2">
            {lang === "ar"
              ? "بعد التسجيل، ستتلقى بريدا إلكترونيا للتأكيد. تحقق من البريد العشوائي إذا لم تتلق شيئا."
              : "Après inscription, vous recevrez un email de confirmation. Pensez à vérifier vos spams si vous ne recevez rien."}
          </p>
        )}
      </div>
    </div>
  );
}

function OrdersTab({ lang }) {
  const [orders, setOrders] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [claimId, setClaimId] = useState("");
  const [claiming, setClaiming] = useState(false);
  const reload = () => axios.get(`${API}/customer/orders`, { withCredentials: true })
    .then((r) => setOrders(r.data || []))
    .catch(() => setOrders([]));
  useEffect(() => { reload(); }, []);

  const submitClaim = async () => {
    const id = claimId.trim().toUpperCase();
    if (!id) return;
    setClaiming(true);
    try {
      const { data } = await axios.post(`${API}/customer/claim-order`, { order_id: id }, { withCredentials: true });
      if (data?.already_linked) toast.info(lang === "ar" ? "هذا الطلب مرتبط مسبقا" : "Cette commande est déjà liée");
      else toast.success(lang === "ar" ? "✅ تم ربط الطلب" : "✅ Commande ajoutée à votre compte");
      setClaimId("");
      setShowClaim(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ar" ? "تعذر ربط الطلب" : "Impossible de réclamer cette commande"));
    } finally { setClaiming(false); }
  };

  if (orders === null) return <p className="text-white/50">…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-white/50">
          {lang === "ar"
            ? "تظهر الطلبات التي تم إجراؤها بنفس البريد الإلكتروني تلقائيا. للطلبات الأخرى، اضغط على «إضافة طلب»."
            : "Les commandes passées avec le même email sont liées automatiquement. Pour les autres, cliquez sur « Ajouter une commande »."}
        </p>
        <button
          onClick={() => setShowClaim((v) => !v)}
          className="text-xs uppercase tracking-widest border border-neon/40 px-3 py-2 hover:bg-neon/10 font-bold"
          data-testid="claim-order-btn"
        >
          📦 {lang === "ar" ? "إضافة طلب" : "Ajouter une commande"}
        </button>
      </div>

      {showClaim && (
        <div className="border border-neon/30 bg-neon/5 p-4 space-y-3" data-testid="claim-order-form">
          <p className="text-xs text-white/80">
            {lang === "ar"
              ? "أدخل رقم طلبك (مثل DZTS-20260516-XXXXXX) الذي تلقيته عبر واتساب. يجب أن يكون رقم هاتفك مطابقا للرقم المسجل في الطلب."
              : "Entrez le numéro de commande (ex: DZTS-20260516-XXXXXX) reçu par WhatsApp. Votre numéro de téléphone doit correspondre à celui enregistré sur la commande."}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={claimId}
              onChange={(e) => setClaimId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitClaim()}
              placeholder="DZTS-AAAAMMJJ-XXXXXX"
              className="flex-1 bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono tracking-widest uppercase focus:border-neon outline-none"
              data-testid="claim-order-input"
            />
            <button
              onClick={submitClaim}
              disabled={!claimId.trim() || claiming}
              className="px-4 py-2 bg-neon text-black font-black uppercase tracking-widest text-xs disabled:opacity-40"
              data-testid="claim-order-confirm"
            >
              {claiming ? "…" : (lang === "ar" ? "إضافة" : "Réclamer")}
            </button>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <p className="text-white/50 border border-dashed border-white/10 p-8 text-center" data-testid="orders-empty">
          {lang === "ar" ? "لا توجد طلبات بعد" : "Aucune commande pour l'instant"}
        </p>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="border border-white/10 p-4 flex flex-wrap items-center gap-3" data-testid={`order-${o.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono text-xs text-neon">{o.id}</div>
                <div className="text-sm mt-1">{(o.items || []).length} {lang === "ar" ? "منتج" : "produits"} · {Math.round(o.total_dzd || 0).toLocaleString()} DZD</div>
                <div className="text-[10px] text-white/40 uppercase tracking-widest mt-1">
                  {(o.created_at || "").split("T")[0]} · {o.status} · {o.payment_status}
                </div>
              </div>
              <a
                href={`${API}/customer/invoice/${o.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs uppercase tracking-widest border border-neon/40 px-3 py-2 hover:bg-neon/10 inline-flex items-center gap-1"
                data-testid={`invoice-${o.id}`}
              >
                <Download className="w-3 h-3" /> {lang === "ar" ? "فاتورة" : "Facture"}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileTab({ lang, user }) {
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    tennis_level: user.tennis_level || "",
    club: user.club || "",
    role: user.role || "joueur",
    contact_pref: user.contact_pref || "whatsapp",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/customer/me`, form, { withCredentials: true });
      toast.success(lang === "ar" ? "تم الحفظ" : "Enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-4 max-w-2xl" data-testid="profile-form">
      <Field label={lang === "ar" ? "الاسم" : "Nom"}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="profile-name" /></Field>
      <Field label={lang === "ar" ? "هاتف" : "Téléphone WhatsApp"}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="profile-phone" /></Field>
      <Field label={lang === "ar" ? "المستوى" : "Niveau"}>
        <Select value={form.tennis_level} onValueChange={(v) => setForm({ ...form, tennis_level: v })}>
          <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid="profile-level"><SelectValue placeholder={lang === "ar" ? "اختر" : "Choisir"} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="debutant">{lang === "ar" ? "مبتدئ" : "Débutant"}</SelectItem>
            <SelectItem value="intermediaire">{lang === "ar" ? "متوسط" : "Intermédiaire"}</SelectItem>
            <SelectItem value="competition">{lang === "ar" ? "تنافسي" : "Compétition"}</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
            <SelectItem value="club">Club</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label={lang === "ar" ? "النادي" : "Mon club"}><Input value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} className="bg-black/40 border-white/10 rounded-none focus-visible:ring-neon" data-testid="profile-club" /></Field>
      <Field label={lang === "ar" ? "الدور" : "Rôle"}>
        <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
          <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid="profile-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="joueur">{lang === "ar" ? "لاعب" : "Joueur"}</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
            <SelectItem value="responsable">{lang === "ar" ? "مسؤول نادي" : "Responsable club"}</SelectItem>
            <SelectItem value="revendeur">{lang === "ar" ? "موزع" : "Revendeur"}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <Field label={lang === "ar" ? "وسيلة الاتصال المفضلة" : "Préférence de contact"}>
        <Select value={form.contact_pref} onValueChange={(v) => setForm({ ...form, contact_pref: v })}>
          <SelectTrigger className="bg-black/40 border-white/10 rounded-none" data-testid="profile-contact-pref"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="phone">{lang === "ar" ? "اتصال" : "Appel"}</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="sm:col-span-2 mt-2">
        <Button onClick={save} disabled={busy} className="bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest" data-testid="profile-save">
          {busy ? "…" : (lang === "ar" ? "حفظ" : "Enregistrer")}
        </Button>
      </div>
    </div>
  );
}

function ReferralTab({ lang }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/customer/referral`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ enabled: false }));
  }, []);
  if (!data) return <p className="text-white/50">…</p>;
  if (!data.enabled)
    return <p className="text-white/50 border border-dashed border-white/10 p-8 text-center">{lang === "ar" ? "نظام الإحالة معطل" : "Le parrainage n'est pas actif."}</p>;

  const copy = () => {
    navigator.clipboard.writeText(data.code);
    toast.success(lang === "ar" ? "نسخ!" : "Copié !");
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="referral-block">
      <div className="border border-neon/40 bg-neon/5 p-6 text-center">
        <p className="text-[10px] uppercase tracking-widest text-neon mb-2">{lang === "ar" ? "رمزي الشخصي" : "Mon code personnel"}</p>
        <div className="font-heading font-black text-4xl sm:text-5xl text-neon mb-3 tracking-widest" data-testid="referral-code">{data.code}</div>
        <p className="text-sm text-white/70 mb-4">
          {lang === "ar"
            ? `يتلقى صديقك تخفيض ${data.discount_pct_for_friend}% (الحد الأدنى ${data.min_order_dzd} دج). تحصل على ${data.owner_reward_pct}% كرمز هدية.`
            : `Votre filleul reçoit -${data.discount_pct_for_friend}% (commande min. ${data.min_order_dzd} DZD). Vous recevez ${data.owner_reward_pct}% en code-cadeau réutilisable.`}
        </p>
        <Button onClick={copy} className="bg-neon text-black hover:opacity-90 rounded-none font-black uppercase tracking-widest" data-testid="referral-copy">
          <Copy className="w-4 h-4 me-2" />{lang === "ar" ? "نسخ الرمز" : "Copier le code"}
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat label={lang === "ar" ? "إحالات" : "Filleuls"} value={data.referrals_count} testid="referral-count" />
        <Stat label={lang === "ar" ? "هداياي" : "Mes cadeaux"} value={(data.cashbacks || []).length} testid="referral-cashbacks-count" />
      </div>
      {(data.cashbacks || []).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/50 mb-2">{lang === "ar" ? "رموز الهدايا الخاصة بي" : "Mes codes-cadeaux"}</p>
          <div className="space-y-2">
            {data.cashbacks.map((c) => (
              <div key={c.id} className="border border-white/10 p-3 flex items-center justify-between text-sm">
                <span className="font-mono text-neon">{c.code}</span>
                <span className="text-xs text-white/50">-{c.discount_pct}% · {c.active ? "actif" : "utilisé"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AddressesTab({ lang }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    axios.get(`${API}/customer/orders`, { withCredentials: true })
      .then((r) => setOrders(r.data || []))
      .catch(() => setOrders([]));
  }, []);
  if (orders === null) return <p className="text-white/50">…</p>;
  // Derive unique addresses from order history
  const seen = new Set();
  const addresses = [];
  for (const o of orders) {
    const a = o.customer || {};
    const key = `${a.address}|${a.wilaya}`;
    if (a.address && !seen.has(key)) {
      seen.add(key);
      addresses.push({ ...a, last_used: o.created_at });
    }
  }
  if (addresses.length === 0)
    return <p className="text-white/50 border border-dashed border-white/10 p-8 text-center">{lang === "ar" ? "لا توجد عناوين بعد" : "Aucune adresse pour l'instant — passez votre 1ère commande."}</p>;
  return (
    <div className="space-y-3 max-w-2xl" data-testid="addresses-list">
      <p className="text-xs text-white/50 mb-3">{lang === "ar" ? "العناوين المستعملة في طلباتك السابقة" : "Adresses utilisées lors de vos commandes précédentes"}</p>
      {addresses.map((a, i) => (
        <div key={i} className="border border-white/10 p-4">
          <div className="font-bold text-sm">{a.name}</div>
          <div className="text-xs text-white/70 mt-1">{a.address}</div>
          <div className="text-xs text-white/50 mt-1">Wilaya {a.wilaya} · {a.phone}</div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ lang, navigate }) {
  const { logout } = useCustomer();
  const [confirming, setConfirming] = useState(false);
  const remove = async () => {
    try {
      await axios.delete(`${API}/customer/me`, { withCredentials: true });
      toast.success(lang === "ar" ? "تم حذف الحساب" : "Compte supprimé");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  return (
    <div className="max-w-md border border-clay/40 bg-clay/5 p-5" data-testid="settings-tab">
      <p className="font-heading font-black uppercase tracking-tight text-clay text-sm mb-2">{lang === "ar" ? "حذف الحساب" : "Supprimer mon compte"}</p>
      <p className="text-xs text-white/60 mb-4">
        {lang === "ar"
          ? "سيتم إخفاء معلوماتك الشخصية. الطلبات القديمة تبقى محفوظة لأسباب محاسبية بدون اسم/هاتف."
          : "Vos données personnelles seront anonymisées. Vos commandes restent conservées pour la comptabilité sans nom/téléphone (RGPD)."}
      </p>
      {!confirming ? (
        <Button onClick={() => setConfirming(true)} variant="outline" className="border-clay/50 text-clay hover:bg-clay/10 rounded-none uppercase tracking-widest" data-testid="delete-confirm-1">
          <Trash2 className="w-4 h-4 me-2" />{lang === "ar" ? "حذف الحساب" : "Supprimer mon compte"}
        </Button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-clay font-bold">{lang === "ar" ? "هل أنت متأكد؟" : "Confirmer la suppression ?"}</p>
          <div className="flex gap-2">
            <Button onClick={remove} className="bg-clay hover:opacity-90 rounded-none uppercase tracking-widest" data-testid="delete-confirm-2">{lang === "ar" ? "نعم، حذف" : "Oui, supprimer"}</Button>
            <Button onClick={() => setConfirming(false)} variant="outline" className="border-white/20 rounded-none uppercase tracking-widest">{lang === "ar" ? "إلغاء" : "Annuler"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60 mb-1.5 block">{label}</Label>{children}</div>;
}
function Stat({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-4 text-center" data-testid={testid}>
      <div className="font-heading font-black text-3xl text-neon">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">{label}</div>
    </div>
  );
}
